import { useMutation, useQuery } from '@apollo/client'
import { Button, Checkbox, FormControl, FormControlLabel, MenuItem, Paper } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import ErrorMessage from '../../../common/error/ErrorMessage'
import FormField from '../../../common/form/FormField'
import { i18n } from '../../index/index'
import { checklistItemsQuery } from '../checklist/checklistQueries'
import {
  createChainReminderMutation,
  getAllChainReminders,
  updateChainReminderMutation,
} from '../reminders/RemindersQueries'
import css from '../reminders/reminders.css'
import {
  ChainReminder,
  ChecklistItemEdge,
  ContinuingChecklistItemType,
  WeekDaysType,
  defaultValues,
  weekDaysMap,
  weekdays,
} from '../types'

interface Props {
  readonly editedChainReminder?: ChainReminder
  readonly onResetEditedChainReminder: () => void
}

type FormData = {
  id?: string
  everyday?: boolean
  checklistItem: string
  reminder: string
  time: string
} & WeekDaysType

export default function ChainRemindersForm({ editedChainReminder, onResetEditedChainReminder }: Props) {
  const {
    watch,
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>(defaultValues)

  const onReset: React.MouseEventHandler<HTMLElement> = (e) => {
    e.preventDefault()
    reset(defaultValues)
    onResetEditedChainReminder()
  }

  const [updateChainReminderMutationFunction] = useMutation(updateChainReminderMutation, {
    refetchQueries: [
      {
        query: getAllChainReminders,
      },
    ],
  })

  const [createChainReminderMutationFunction] = useMutation(createChainReminderMutation, {
    refetchQueries: [
      {
        query: getAllChainReminders,
      },
    ],
  })

  const onSubmit = handleSubmit(async (formData, e) => {
    e?.preventDefault()
    let newWeekdays = [...weekdays]
    if (!formData.everyday) {
      newWeekdays = newWeekdays.filter((day) => {
        return formData[day]
      })
    }

    const data = {
      name: formData.reminder,
      time: formData.time,
      weekdays: newWeekdays,
    }

    try {
      await (formData.id
        ? updateChainReminderMutationFunction({
            variables: { id: formData.id, data },
          })
        : createChainReminderMutationFunction({
            variables: { data },
          }))
      reset(defaultValues)
      onResetEditedChainReminder()
    } catch (err) {
      setError('root.serverError', {
        message: i18n.t('admin.reminders.saveError'),
      })
    }
  })

  useEffect(() => {
    if (editedChainReminder) {
      reset({
        id: editedChainReminder.id,
        reminder: editedChainReminder.name,
        time: editedChainReminder.time,
        everyday: editedChainReminder.weekdays.length === 7,
        ...Object.assign(
          {},
          ...weekdays.map((weekday) => ({
            [weekday]: editedChainReminder.weekdays.includes(weekday),
          })),
        ),
      })
    }
  }, [editedChainReminder, reset])

  const everyday = watch('everyday')
  const { data } = useQuery<{
    checklistItems: { total: number; edges: ChecklistItemEdge[] }
    continuingChecklistItems: ContinuingChecklistItemType
  }>(checklistItemsQuery, {
    fetchPolicy: 'no-cache',
  })

  return (
    <Paper className={css.component}>
      <form onSubmit={onSubmit}>
        <div className={css.inner}>
          <FormControl className={css.formFields}>
            <FormField
              fullWidth
              label={i18n.t('admin.reminders.checklistItems')}
              select
              variant='outlined'
              name='checklistItem'
              control={control}
              setValueAs={(id) => {
                const nodeCandidate = data?.checklistItems.edges.find((edge) => edge.node.id === id)?.node

                if (nodeCandidate) {
                  if (nodeCandidate.days.length === 7) {
                    setValue('everyday', true)
                  } else {
                    setValue('everyday', false)
                    weekdays.forEach((day) => {
                      const dayCandidate = nodeCandidate.days.find((nodeDay) => nodeDay === weekDaysMap[day])
                      if (dayCandidate) {
                        setValue(day, true)
                        return
                      }
                      setValue(day, false)
                    })
                  }
                  setValue('reminder', nodeCandidate.description)
                  setValue('time', '')
                  setValue('id', undefined)
                }
                return id
              }}
            >
              <MenuItem key={'default'} value='default'>
                -
              </MenuItem>
              {data?.checklistItems.edges.map(({ node: { id, description } }) => {
                return (
                  <MenuItem key={id} value={id}>
                    {description}
                  </MenuItem>
                )
              })}
            </FormField>
            <div className={css.inline}>
              <FormField
                name='reminder'
                control={control}
                label={i18n.t('admin.reminders.reminder')}
                variant='outlined'
                margin='normal'
                rules={{ required: true }}
              />
              <FormField
                name='time'
                control={control}
                label={i18n.t('admin.reminders.time')}
                variant='outlined'
                margin='normal'
                type='time'
                rules={{ required: true }}
              />
            </div>
            <div className={css.grid}>
              <Controller
                name='everyday'
                key='everyday'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label={i18n.t('admin.reminders.all')}
                    control={<Checkbox {...field} value={undefined} checked={field.value ?? false} />}
                  />
                )}
              />
              {!everyday &&
                weekdays.map((key) => {
                  return (
                    <Controller
                      name={key}
                      key={key}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          label={i18n.t(`admin.reminders.${key}`)}
                          key={key}
                          control={<Checkbox {...field} value={undefined} checked={field.value ?? false} />}
                        />
                      )}
                    />
                  )
                })}
            </div>
            <div className={css.actionButtonContainer}>
              <Button color='primary' type='submit' variant='contained' size='large' disabled={isSubmitting}>
                {i18n.t('buttons.save')}
              </Button>
              <Button
                color='secondary'
                type='reset'
                variant='contained'
                size='large'
                disabled={isSubmitting}
                onClick={onReset}
              >
                {i18n.t('buttons.reset')}
              </Button>
            </div>
            {errors.root?.['serverError']?.message && <ErrorMessage message={errors.root['serverError'].message} />}
          </FormControl>
        </div>
      </form>
    </Paper>
  )
}
